import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'react-feather';

/**
 * V3 pager replacing react-paginate.
 *
 *   page       — current page (0-based)
 *   pageCount  — total pages
 *   onChange   — (nextPage) => void
 *   totalItems — total row count (for the summary)
 *   pageSize   — rows per page (for the summary)
 *   itemLabel  — noun for the summary (default "rows")
 */
const windowed = (page, pageCount) => {
    const pages = new Set([0, pageCount - 1, page, page - 1, page + 1]);
    const sorted = [...pages].filter((p) => p >= 0 && p < pageCount).sort((a, b) => a - b);
    const out = [];
    let prev = null;
    for (const p of sorted) {
        if (prev !== null && p - prev > 1) out.push('…');
        out.push(p);
        prev = p;
    }
    return out;
};

const ReportPagination = ({ page, pageCount, onChange, totalItems = 0, pageSize = 50, itemLabel = 'rows' }) => {
    const { t } = useTranslation();
    if (pageCount <= 1) return null;
    const from = page * pageSize + 1;
    const to = Math.min((page + 1) * pageSize, totalItems);

    return (
        <div className="v3-sadmin-pagination">
            <span>{t('Showing {{from}}–{{to}} of {{total}} {{items}}', { from: from.toLocaleString(), to: to.toLocaleString(), total: totalItems.toLocaleString(), items: t(itemLabel) })}</span>
            <div className="v3-sadmin-pagination-controls">
                <button
                    type="button"
                    className="v3-sadmin-page-btn"
                    disabled={page === 0}
                    onClick={() => onChange(page - 1)}
                    aria-label={t('Previous page')}
                >
                    <ChevronLeft size={14} />
                </button>
                {windowed(page, pageCount).map((p, i) =>
                    p === '…' ? (
                        <span key={`gap-${i}`} style={{ padding: '0 4px', color: 'var(--v3-text-dim)' }}>…</span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            className={`v3-sadmin-page-btn ${p === page ? 'active' : ''}`}
                            onClick={() => onChange(p)}
                            aria-current={p === page ? 'page' : undefined}
                        >
                            {p + 1}
                        </button>
                    ),
                )}
                <button
                    type="button"
                    className="v3-sadmin-page-btn"
                    disabled={page >= pageCount - 1}
                    onClick={() => onChange(page + 1)}
                    aria-label={t('Next page')}
                >
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
};

export default ReportPagination;
